import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import "../fixtures.css";

function getTypeBadgeClass(type) {
  const t = type.toLowerCase();
  if (t.includes("cup finals") || t.includes("final")) return "finals";
  if (t.includes("cup sf") || t.includes("semi")) return "sf";
  if (t.includes("cup qf") || t.includes("quarter")) return "qf";
  if (t === "pool") return "pool";
  return "place";
}

function getRowClass(type) {
  const t = type.toLowerCase();
  if (t.includes("cup finals") || t.includes("final")) return "finals";
  if (t.includes("cup sf") || t.includes("semi")) return "sf";
  return "";
}

function MatchRow({ match }) {
  const rowClass = getRowClass(match.type);
  const badgeClass = getTypeBadgeClass(match.type);
  const played = match.score_a !== null && match.score_b !== null;

  return (
    <div className={`rug-match-row ${rowClass}`}>
      {/* meta: always stays on first line */}
      <div className="rug-match-meta">
        <span className="rug-match-no">#{match.game_no}</span>
        <span className="rug-match-time">{match.time}</span>
        <span className={`rug-type-badge ${badgeClass}`}>
          {match.pool ? `Pool ${match.pool}` : match.type}
        </span>
      </div>

      {/* teams + score: flows after meta on desktop, wraps to own row on mobile */}
      <div className="rug-match-teams">
        {played ? (
          <div>
            <span className={match.score_a > match.score_b ? "rug-team-winner" : "rug-team-loser"}>
              {match.team_a}
            </span>
            <span className="rug-score-block">
              <span className="rug-score">{match.score_a}</span>
              <span className="rug-score-sep">–</span>
              <span className="rug-score">{match.score_b}</span>
            </span>
            <span className={match.score_b > match.score_a ? "rug-team-winner" : "rug-team-loser"}>
              {match.team_b}
            </span>
          </div>
        ) : (
          <div>
            <span className="rug-team-name">{match.team_a}</span>
            <span className="rug-vs">vs</span>
            <span className="rug-team-name">{match.team_b}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function EventFixtures({ event }) {
  const [activeDay, setActiveDay] = useState("day1");

  const dayData = event.schedule[activeDay];
  const breaks = dayData.breaks || [];

  // Insert break markers between time blocks
  const renderMatches = () => {
    const rows = [];
    dayData.matches.forEach((match, idx) => {
      rows.push(<MatchRow key={match.game_no} match={match} />);
    });
    return rows;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, color: "#24528e", fontSize: 15 }}>
          {event.section}
        </div>
        <div style={{ color: "#e65c00", fontSize: 13, fontWeight: 600, marginTop: 2 }}>
          {event.dates}
        </div>
      </div>

      {/* Pools */}
      <div className="rug-section-label">Pools</div>
      <div className="rug-pool-grid">
        {Object.entries(event.pools).map(([pool, teams]) => (
          <div key={pool} className="rug-pool-card">
            <div className="rug-pool-header">Pool {pool}</div>
            <ul className="rug-pool-teams">
              {teams.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Day Selector */}
      <div className="rug-section-label">Schedule</div>
      <div className="rug-day-selector">
        <button
          className={`rug-day-btn ${activeDay === "day1" ? "active" : ""}`}
          onClick={() => setActiveDay("day1")}
        >
          Day 1 — {event.schedule.day1.date}
        </button>
        <button
          className={`rug-day-btn ${activeDay === "day2" ? "active" : ""}`}
          onClick={() => setActiveDay("day2")}
        >
          Day 2 — {event.schedule.day2.date}
        </button>
      </div>

      {/* Matches */}
      <div className="rug-match-list">{renderMatches()}</div>

      {/* Breaks */}
      {breaks.length > 0 && (
        <div className="rug-note" style={{ marginTop: 10 }}>
          <strong>Breaks:</strong> {breaks.join(" | ")}
        </div>
      )}

      {/* Presentation ceremony */}
      {dayData.presentation_ceremony && (
        <div className="rug-note" style={{ marginTop: 6 }}>
          <strong>Presentation Ceremony:</strong> {dayData.presentation_ceremony}
        </div>
      )}

      {/* Post-pool note */}
      {activeDay === "day2" && event.post_pool_note && (
        <div className="rug-note warn" style={{ marginTop: 6 }}>
          {event.post_pool_note}
        </div>
      )}

      {/* General note */}
      <div className="rug-note warn" style={{ marginTop: 6 }}>
        {event.general_note}
      </div>
    </div>
  );
}

export default function MatchFixtures() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/data/sen-nat-rug-7.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load fixtures");
        return res.json();
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="rug-note warn" style={{ margin: 16 }}>
        Could not load fixtures: {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24, color: "#888", fontSize: 13 }}>
        Loading fixtures…
      </div>
    );
  }

  const innerTabs = [
    { label: "Women", event: data.events.women },
    { label: "Men", event: data.events.men },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a" }}>
          {data.tournament}
        </div>
        <div style={{ fontSize: 13, color: "#555", marginTop: 3 }}>
          {data.venue}
        </div>
      </div>

      <Tabs variant="enclosed">
        <TabList style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {innerTabs.map((t, i) => (
            <Tab
              key={i}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: "5px 24px",
              }}
              _selected={{ bg: "#24528e", color: "white" }}
            >
              {t.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels
          style={{
            border: "1px solid #cfd9de",
            borderRadius: "0 0 5px 5px",
          }}
        >
          {innerTabs.map((t, i) => (
            <TabPanel key={i}>
              <EventFixtures event={t.event} />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </div>
  );
}
